<template>
  <div class="app-root">
    <!-- Calque de l'écran précédent pendant un glissé de retour depuis le bord.
         Vide et invisible le reste du temps. Il est monté en permanence, et
         non inséré à la volée : ce conteneur est celui que Vue rapièce, et
         un enfant qui apparaît et disparaît sous ses pieds lui ferait
         calculer ses points d'insertion de travers. Voir usePageSnapshot. -->
    <div class="page-snapshot" aria-hidden="true" />
    <AuthGuard>
      <!-- Les deux pages coexistent le temps du glissement : `Transition` sans
           `mode`, donc pas d'`out-in`, sinon la sortante disparaîtrait avant
           que l'entrante n'arrive et il n'y aurait rien à croiser. `:css` à
           faux rend la bascule instantanée sans classe ni attente. -->
      <router-view v-slot="{ Component, route }">
        <!-- `@after-enter` : le moment exact où plus rien ne glisse. Ce que
             `onPageEnterSettled` attend pour ouvrir un clavier sans saccader
             la poussée. -->
        <Transition :name="transitionName" :css="transitionEnabled" @after-enter="endPageTransition">
          <!-- Clé sur la route de PREMIER niveau, pas sur le chemin complet :
               tout `/tabs/**` partage l'enregistrement `/tabs`, donc TabsPage
               n'est ni remontée ni animée quand c'est sa sortie interne qui
               change. -->
          <component :is="Component" :key="route.matched[0]?.path ?? route.fullPath" />
        </Transition>
      </router-view>
    </AuthGuard>
    <!-- Les toasts descendent du HAUT, sous l'encoche. En bas, ils passaient
         par-dessus les tâches qu'on était en train de cocher. -->
    <Toaster position="top-center" :offset="TOAST_OFFSET" :mobile-offset="TOAST_OFFSET" />

    <!-- Reward Notifications Queue -->
    <RewardNotificationQueue />

    <!-- Les pièces qui volent jusqu'au compteur (cf. lib/coinFlight). -->
    <CoinFlightLayer />

    <!-- Simple Update Modal -->
    <UpdateModal v-model="showUpdateModal" @update="openStore" />

    <!-- Feedback modal (sentiment gate + app store rating) -->
    <FeedbackModal />
    <ContactSheet />

    <!-- Notification permission pre-prompt (validation reminder) -->
    <NotificationPermissionSheet />

    <!-- Les nouveautés de la version, une fois après la mise à jour (#408). -->
    <WhatsNewSheet />

    <!-- Explication « ceci est une fonctionnalité Harmonia+ », ouverte par le
         badge H+ depuis n'importe quel écran. Montée ici une seule fois. -->

    <!-- « Pourquoi tu refermes ? », posée après un paywall refermé sans achat.
         Montée ici une seule fois : une douzaine d'écrans ouvrent le paywall,
         et plusieurs sont déjà à l'intérieur d'une feuille. -->
    <PaywallDismissReasonModal />

    <!-- « Commence avec {prénom} » : prend la place de la question précédente
         quand un foyer pas encore activé referme le paywall du sélecteur
         d'enfant (HARMONIA-93). Même raison d'être montée ici. -->
    <StartWithOneSheet />

    <!-- Le code parent du mode enfant : créé à l'entrée, demandé à la sortie.
         Montée ici une seule fois, parce que la sortie change de section et
         emporterait une feuille montée dans les écrans enfant (HARMONIA-87). -->
    <ParentCodeSheet />

    <!-- Screenshot dev panel (only visible when logged in with a screenshot-* account) -->
    <ScreenshotPanel />

  </div>
</template>

<script setup lang="ts">
import { HOME_PATH } from '@/router/paths';
import { onMounted, watch } from 'vue';
import { debugLog } from '@/utils/logger'
import { useRouter, useRoute } from 'vue-router';
import { App as CapacitorApp } from '@capacitor/app';
import { useVersionCheck } from '@/composables/useVersionCheck';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import AuthGuard from '@/components/auth/AuthGuard.vue';
import RewardNotificationQueue from '@/components/rewards/RewardNotificationQueue.vue';
import CoinFlightLayer from '@/components/coins/CoinFlightLayer.vue'
import UpdateModal from '@/components/modals/UpdateModal.vue';
import FeedbackModal from '@/components/feedback/FeedbackModal.vue';
import ContactSheet from '@/components/feedback/ContactSheet.vue';
import ParentCodeSheet from '@/components/common/ParentCodeSheet.vue';
import NotificationPermissionSheet from '@/components/notifications/NotificationPermissionSheet.vue';
import WhatsNewSheet from '@/components/whatsNew/WhatsNewSheet.vue';
import PaywallDismissReasonModal from '@/components/paywall/PaywallDismissReasonModal.vue';
import StartWithOneSheet from '@/components/paywall/StartWithOneSheet.vue';
import { setupNotificationTapHandler } from '@/services/push-token.service';
import ScreenshotPanel from '@/components/screenshot/ScreenshotPanel.vue';
import { Toaster } from '@/components/ui/sonner';
// Sous la zone sûre du haut (encoche, barre d'état), plus 8px d'air. La variable
// injectée par Capacitor sur Android d'abord, `env()` natif sur iOS ensuite.
const TOAST_OFFSET = {
  top: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 8px)',
  left: '16px',
  right: '16px',
};
import { useAppContextStore } from '@/stores/appContext';
import { useAuthStore } from '@/stores/auth';
import { initializeStorage } from '@/utils/storage-setup';
import { useKeyboardViewport } from '@/composables/useKeyboardViewport';
import { checkPremiumExpiration } from '@/services/premium.service';
import { notifyAppPaused, notifyAppResumed } from '@/services/app-resume.service';
import { useScreenOrientation } from '@/composables/useScreenOrientation';
import { useAnalytics } from '@/composables/useAnalytics';
import { useOnboardingController } from '@/composables/useOnboardingController';
import { perfMark, perfIncrement, perfLogSummary } from '@/utils/perf-metrics';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/lib/supabase';
import { dailyCompletionService } from '@/services/dailyCompletion.service';
import { todayStr, toDateStr } from '@/services/date.service';
import { handleScreenshotDeepLink, consumePendingScreenshotIntent } from '@/services/screenshot-deeplink';
import { PREVIEW_INTENT_KEY } from '@/services/app-preview/preview-data';
import { resolveGoTarget } from '@/services/go-deeplink';
import { screenshotUrlFromSearch } from '@/services/screenshot-web-bridge';
import { useHaptics } from '@/composables/useHaptics';
import { localeService } from '@/services/locale.service';
import { useOnboardingFinalize } from '@/composables/useOnboardingFinalize';
import { useSwipeBack } from '@/composables/useSwipeBack';
import { usePageTransition } from '@/composables/usePageTransition';

// Router
const router = useRouter();
const route = useRoute();


// App context store
const appContextStore = useAppContextStore();
const authStore = useAuthStore();

// Version management
const {
  showUpdateModal,
  checkForUpdates,
  openStore
} = useVersionCheck();

// Initialize keyboard viewport management (side effects run inside the composable)
useKeyboardViewport();

// Initialize screen orientation management (side effects run inside the composable)
useScreenOrientation();

// Initialize Analytics
const analytics = useAnalytics();

// Initialize app context when authentication changes
const initializeAppContext = async () => {
  // Wait for Supabase session restoration before touching protected data.
  // Otherwise the cached user from localStorage triggers loads while
  // supabase.auth.getUser() still returns null, producing "User not authenticated".
  await authStore.waitForAuth();

  if (authStore.user) {
    try {
      perfIncrement('contextLoads');
      perfMark('app-context-load-start');
      await appContextStore.loadUserHouseholds(authStore.user.id);
      perfMark('app-context-load-end');
      perfLogSummary('app-context-ready');

      // Hydrate the whole household into the stores BEFORE the splash hides, so
      // the first navigation is instant (pages read warm local state).
      const hid = appContextStore.currentHouseholdId;
      if (hid) {
        const { hydrateHousehold } = await import('@/services/householdHydration');
        // Hold the splash for hydration, but bound it: a single stalled query must
        // not wedge the splash. Realtime + pull-to-refresh reconcile anything that
        // didn't finish in time; pages still render from whatever has loaded.
        await Promise.race([
          hydrateHousehold(hid),
          new Promise<void>((resolve) => setTimeout(resolve, 4000)),
        ]);
      }

      // Fin d'abonnement OU fin d'essai : la garde `is_premium` d'avant
      // laissait passer les foyers en essai, qui ne sont jamais premium en base.
      const h = appContextStore.currentHousehold;
      if (h) {
        await checkPremiumExpiration(h);
      }

      // Hors du chemin critique : aligne user_profiles.language sur la langue
      // affichée. C'est cette colonne que lisent les edge functions d'emails, et
      // elle pouvait rester bloquée sur une autre langue que celle de l'app.
      void localeService.loadFromDatabase();
    } catch (error) {
      console.error('❌ [App.vue] Error initializing app context:', error);
    }
  } else {
    appContextStore.resetContext();
  }
};

// Watch for authentication changes to update app context and Analytics
watch(() => authStore.user, async (newUser, oldUser) => {
  // console.log('👤 [App.vue] Auth user changed:', {
  //   newUserId: newUser?.id,
  //   oldUserId: oldUser?.id,
  //   timestamp: new Date().toISOString()
  // });

  // Update Analytics user ID when user changes
  if (newUser?.id !== oldUser?.id) {
    if (newUser?.id) {
      await analytics.setUserId(newUser.id);
      // Set user properties
      if (newUser.language) {
        await analytics.setUserProperty('language', newUser.language);
      }
    } else {
      await analytics.setUserId(null);
    }
  }

  // Éviter les appels redondants si l'utilisateur est le même
  if (newUser?.id === oldUser?.id) {
    // console.log('🔄 [App.vue] Same user ID, skipping reinitialization');
    return;
  }

  if (newUser !== oldUser) {
    // console.log('🔄 [App.vue] Reinitializing app context due to auth change');
    await initializeAppContext();
  }
}, { immediate: true });

// Retour haptique léger (10 ms) sur chaque ion-button, installé une fois au
// niveau document plutôt que bouton par bouton. Capture pour passer outre les
// stopPropagation éventuels des handlers ; les clics dans le shadow DOM sont
// re-ciblés sur l'hôte ion-button, donc closest() suffit. Les tabs (ion-tab-
// button) gardent leur propre haptic dans TabsPage.
const haptics = useHaptics();
const onGlobalButtonClick = (e: Event) => {
  if ((e.target as HTMLElement)?.closest?.('ion-button')) {
    void haptics.impactLight();
  }
};

// Écouter les événements de cycle de vie de l'app (iOS/Android)
// Retour arrière au glissé depuis le bord gauche, monté une seule fois.
useSwipeBack();
const { transitionName, transitionEnabled, endPageTransition } = usePageTransition();

onMounted(() => {
  document.addEventListener('click', onGlobalButtonClick, { capture: true });

  // Navigate to the validation screen when a push notification is tapped.
  void setupNotificationTapHandler((route: string) => {
    router.push(route);
  });

  // Listener pour quand l'app revient au foreground (iOS/Android)
  CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
    debugLog('[App.vue] App state changed - isActive:', isActive);

    if (isActive) {
      await analytics.logEvent('app_resumed');
      // Contexte + premium + refreshes : centralisés et dédupliqués avec le
      // handler focus/visibilitychange web (main.ts) qui tire aussi sur device.
      notifyAppResumed();
    } else {
      // If the user backgrounds the app mid-onboarding, record it as a (silent)
      // abandon so Amplitude captures background drop-offs, not just explicit
      // cancels. No-op outside onboarding / on the success step.
      try { useOnboardingController().trackAbandonOnPause(); } catch { /* noop */ }
      notifyAppPaused();
    }
  });

  // Listener pour les Universal Links/App Links (magic links, etc.)
  CapacitorApp.addListener('appUrlOpen', async (event) => {
    debugLog('🔗 [App.vue] Universal link opened:', event.url);

    try {
      // Screenshot automation deep link.
      if (event.url.includes('screenshot')) {
        const handled = await handleScreenshotDeepLink(event.url, router);
        if (handled) return;
      }

      // CTA emails marketing/drip : chemin dédié /go associé à l'app.
      // Pas un flow auth (aucun code à échanger) → simple navigation.
      // Construit l'URL de façon défensive comme les branches auth ci-dessous.
      let goUrl: URL | null = null;
      try {
        goUrl = new URL(event.url);
      } catch {
        try {
          goUrl = new URL(event.url.replace('com.withharmonia.app://', 'https://harmonia.app/'));
        } catch {
          goUrl = null;
        }
      }
      if (goUrl && goUrl.pathname === '/go') {
        debugLog('🚀 [App.vue] /go deep link detected');
        router.replace(
          resolveGoTarget(goUrl.searchParams.get('to'), {
            code: goUrl.searchParams.get('code'),
          }),
        );
        return;
      }

      // Vérifier si c'est un lien de réinitialisation de mot de passe
      if (event.url.includes('/auth/reset-password') || event.url.includes('auth/reset-password')) {
        debugLog('🔑 [App.vue] Password reset deep link detected');
        try {
          await Browser.close();
        } catch (e) {
          // Browser might not be open, that's ok
        }

        let url: URL;
        try {
          url = new URL(event.url);
        } catch (e) {
          const cleanUrl = event.url.replace('com.withharmonia.app://', 'https://harmonia.app/');
          url = new URL(cleanUrl);
        }

        const code = url.searchParams.get('code');
        if (code) {
          router.replace(`/auth/reset-password?code=${code}`);
        } else {
          router.replace('/auth/reset-password');
        }
        return;
      }

      // Vérifier si c'est un callback d'authentification (OAuth a ouvert le Browser)
      if (event.url.includes('/auth/callback') || event.url.includes('auth/callback')) {
        // Fermer le browser Capacitor uniquement dans ce cas (évite "No active window" après Apple Sign In natif)
        try {
          await Browser.close();
          debugLog('✅ [App.vue] Capacitor Browser closed');
        } catch (e) {
          // Browser might not be open, that's ok
        }
        debugLog('🔐 [App.vue] Auth callback detected via Universal Link');

        // Extraire les paramètres depuis l'URL
        let url: URL;
        try {
          url = new URL(event.url);
        } catch (e) {
          // Si l'URL n'est pas valide, essayer de la construire
          const cleanUrl = event.url.replace('com.withharmonia.app://', 'https://harmonia.app/');
          url = new URL(cleanUrl);
        }

        // Vérifier si c'est un flow OAuth (code)
        const code = url.searchParams.get('code');

        if (code) {
          debugLog('🔐 [App.vue] OAuth flow detected, exchanging code for session...');

          // Add delay for iOS Simulator network stability
          const isSimulator = false; // Could detect simulator here if needed
          const waitTime = isSimulator ? 3000 : 1000;
          debugLog(`⏳ [App.vue] Waiting ${waitTime}ms for network to stabilize...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));

          // Échanger le code contre une session avec retry logic pour iOS Simulator
          let retries = 5;
          let lastError: any;
          let sessionData: any;

          // Le bloc entier est enveloppé : un code PKCE ne s'échange qu'UNE
          // fois. S'il a déjà été consommé (deep link livré deux fois, reprise
          // après un cold start, AuthCallbackPage montée en parallèle), le
          // second échange échoue alors que la session est déjà établie.
          // Sans ce filet, l'utilisateur atterrissait sur /login?error= pour
          // une connexion qui avait pourtant réussi. Même logique que
          // AuthCallbackPage, qui retombait déjà sur getSession().
          try {
            while (retries > 0) {
              try {
                const { data, error } = await supabase.auth.exchangeCodeForSession(code);

                if (error) {
                  lastError = error;
                  console.error(`❌ [App.vue] Code exchange error (attempt ${6 - retries}/5):`, error);

                  // Retry on network errors (status 0 = network error)
                  const isNetworkError =
                    error.status === 0 ||
                    error.name?.includes('Fetch') ||
                    error.name?.includes('Network') ||
                    error.message?.includes('Load failed') ||
                    error.message?.includes('network');

                  if (isNetworkError && retries > 1) {
                    retries--;
                    const waitTime = 3000;
                    debugLog(`🔄 [App.vue] Network error detected, retrying in ${waitTime / 1000}s... (${retries} attempts left)`);
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                    continue;
                  }

                  throw error;
                }

                sessionData = data;
                debugLog('✅ [App.vue] Code exchanged for session successfully');
                break; // Success, exit retry loop
              } catch (err: any) {
                lastError = err;
                const isNetworkError =
                  err.status === 0 ||
                  err.name?.includes('Fetch') ||
                  err.name?.includes('Network') ||
                  err.message?.includes('Load failed') ||
                  err.message?.includes('network');

                if (isNetworkError && retries > 1) {
                  retries--;
                  const waitTime = 3000;
                  debugLog(`🔄 [App.vue] Network error caught, retrying in ${waitTime / 1000}s... (${retries} attempts left)`);
                  await new Promise(resolve => setTimeout(resolve, waitTime));
                } else {
                  // Non-network error or last retry
                  console.error('❌ [App.vue] Non-network error or all retries exhausted:', err);
                  throw err;
                }
              }
            }

            if (retries === 0 && lastError) {
              console.error('❌ [App.vue] All retry attempts failed');
              throw lastError;
            }

            if (!sessionData || !sessionData.session) {
              throw new Error('No session received after code exchange');
            }
          } catch (exchangeErr) {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw exchangeErr;
            debugLog('✅ [App.vue] Code déjà consommé, session existante réutilisée');
          }

          // Attendre que l'auth store se mette à jour
          await authStore.waitForAuth();

          debugLog('✅ [App.vue] Auth state updated');

          // Attendre que getUser() soit disponible (important pour éviter les erreurs "User not authenticated")
          debugLog('⏳ [App.vue] Verifying user session is fully propagated...');
          let getUserRetries = 10;
          let userAvailable = false;

          while (getUserRetries > 0 && !userAvailable) {
            const { data: { user }, error: getUserError } = await supabase.auth.getUser();

            if (getUserError) {
              console.warn(`⚠️ [App.vue] getUser error (${11 - getUserRetries}/10):`, getUserError);
            }

            if (user) {
              debugLog('✅ [App.vue] User session fully propagated');
              userAvailable = true;
              break;
            }

            getUserRetries--;
            if (getUserRetries > 0) {
              // Attendre un peu avant de réessayer
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }

          if (!userAvailable) {
            console.error('❌ [App.vue] User session not available after retries');
            throw new Error('User session not available');
          }

          debugLog('✅ [App.vue] Ready to redirect...');

          // Vérifier si on a des données pending d'onboarding
          const hasPendingOnboarding = localStorage.getItem('pending_family_name');

          if (hasPendingOnboarding) {
            debugLog('➡️ [App.vue] Has pending onboarding, redirecting to verify-email');
            router.replace('/verify-email');
            return;
          }

          // Don't override an in-progress join family flow: during /join the
          // account is created (auth change) BEFORE acceptInvitation adds the
          // household, so a "no household" check here would lose the race and
          // wrongly redirect to welcome. The join page handles its own redirect
          // to /join/role. Must be checked BEFORE the no-household redirect —
          // et avant la finalisation d'onboarding, qui pousserait un joiner sur
          // le dashboard au lieu de /join/role.
          if (router.currentRoute.value.path.startsWith('/join/')) {
            debugLog('[App.vue] In join flow, leaving redirect to the page handler');
            return;
          }

          // Onboarding en cours : commit du brouillon + sortie propre du
          // contrôleur (cf. finalizeAfterExternalAuth).
          if (await useOnboardingFinalize().finalizeAfterExternalAuth()) return;

          // Vérifier si c'est un nouvel utilisateur (pas de households)
          if (authStore.user?.id) {
            debugLog('🔍 [App.vue] Checking if user has households...');
            // forceReloadContext (pas loadUserHouseholds) : le cache TTL 30s peut
            // servir un [] périmé peuplé quand le foyer n'existait pas encore.
            await appContextStore.forceReloadContext(authStore.user.id);

            const hasHouseholds = appContextStore.availableHouseholds.length > 0;
            debugLog('🏠 [App.vue] User has households:', hasHouseholds);

            if (!hasHouseholds) {
              debugLog('[App.vue] User with no household, redirecting to /welcome?logged-in-no-household');
              router.replace('/welcome?logged-in-no-household');
              return;
            }
          }

          debugLog('➡️ [App.vue] Existing user with households, redirecting to dashboard');
          router.replace(HOME_PATH);

          return;
        }

        // Fallback: chercher access_token et refresh_token (flow implicite)
        const hashPart = event.url.split('#')[1];
        let access_token: string | null = null;
        let refresh_token: string | null = null;

        if (hashPart) {
          const hashParams = new URLSearchParams(hashPart);
          access_token = hashParams.get('access_token');
          refresh_token = hashParams.get('refresh_token');
        }

        if (!access_token) {
          access_token = url.searchParams.get('access_token');
          refresh_token = url.searchParams.get('refresh_token');
        }

        debugLog('🔐 [App.vue] Tokens extracted:', {
          hasAccessToken: !!access_token,
          hasRefreshToken: !!refresh_token
        });

        if (access_token && refresh_token) {
          debugLog('🔐 [App.vue] Setting session with extracted tokens...');
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token
          });

          if (error) {
            console.error('❌ [App.vue] Error setting session:', error);
            throw error;
          }

          debugLog('✅ [App.vue] Session set successfully from deep link');

          // Attendre que l'auth store se mette à jour
          await authStore.waitForAuth();

          debugLog('✅ [App.vue] Auth state updated');

          // Attendre que getUser() soit disponible (important pour éviter les erreurs "User not authenticated")
          debugLog('⏳ [App.vue] Verifying user session is fully propagated...');
          let getUserRetries3 = 10;
          let userAvailable3 = false;

          while (getUserRetries3 > 0 && !userAvailable3) {
            const { data: { user }, error: getUserError } = await supabase.auth.getUser();

            if (getUserError) {
              console.warn(`⚠️ [App.vue] getUser error (${11 - getUserRetries3}/10):`, getUserError);
            }

            if (user) {
              debugLog('✅ [App.vue] User session fully propagated');
              userAvailable3 = true;
              break;
            }

            getUserRetries3--;
            if (getUserRetries3 > 0) {
              // Attendre un peu avant de réessayer
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }

          if (!userAvailable3) {
            console.error('❌ [App.vue] User session not available after retries');
            throw new Error('User session not available');
          }

          debugLog('✅ [App.vue] Ready to redirect...');

          // Vérifier si on a des données pending d'onboarding
          const hasPendingOnboarding = localStorage.getItem('pending_family_name');

          if (hasPendingOnboarding) {
            debugLog('➡️ [App.vue] Has pending onboarding, redirecting to verify-email');
            router.replace('/verify-email');
            return;
          }

          // Don't override an in-progress join family flow (cf. autre branche
          // ci-dessus) : pendant /join, le compte est créé avant qu'acceptInvitation
          // ajoute le foyer → une vérif "pas de foyer" ici perdrait la course.
          if (router.currentRoute.value.path.startsWith('/join/')) {
            debugLog('[App.vue] In join flow, leaving redirect to the page handler');
            return;
          }

          // Onboarding en cours : commit du brouillon + sortie propre du
          // contrôleur (cf. finalizeAfterExternalAuth). Placé AVANT la
          // vérification "pas de foyer" : le foyer n'existe précisément pas
          // encore tant que le brouillon n'est pas commité.
          if (await useOnboardingFinalize().finalizeAfterExternalAuth()) return;

          // Vérifier si c'est un nouvel utilisateur (pas de households)
          if (authStore.user?.id) {
            debugLog('🔍 [App.vue] Checking if user has households...');
            // forceReloadContext (pas loadUserHouseholds) : cache TTL 30s peut servir
            // un [] périmé peuplé quand le foyer n'existait pas encore.
            await appContextStore.forceReloadContext(authStore.user.id);

            const hasHouseholds = appContextStore.availableHouseholds.length > 0;
            debugLog('🏠 [App.vue] User has households:', hasHouseholds);

            if (!hasHouseholds) {
              debugLog('[App.vue] User with no household, redirecting to /welcome?logged-in-no-household');
              router.replace('/welcome?logged-in-no-household');
              return;
            }
          }

          // Le flow /join est déjà court-circuité par l'early-return plus haut
          // (cf. ligne ~552) ; arrivé ici, l'utilisateur a des foyers et n'est
          // pas dans /join → redirection dashboard.
          debugLog('➡️ [App.vue] Existing user with households, redirecting to dashboard');
          router.replace(HOME_PATH);
        } else {
          console.error('❌ [App.vue] No tokens found in deep link URL');
        }
      }
    } catch (error) {
      console.error('❌ [App.vue] Error handling app URL open:', error);
    }
  });

  debugLog('[App.vue] App state listener registered for iOS/Android');
  debugLog('[App.vue] Deep link listener registered for auth callbacks');
});

// Watch for route changes and track screen views
watch(() => route.path, async (newPath) => {
  // console.log('🛣️ [App.vue] Route changed:', {
  //   from: oldPath,
  //   to: newPath,
  //   timestamp: new Date().toISOString()
  // });

  // Track screen view in Analytics
  if (newPath) {
    // Clean up the path and ensure we have a valid screen name
    let screenName = newPath.replace(/^\//, '').replace(/\/$/, '') || 'home';
    // Replace slashes with underscores for better analytics tracking
    screenName = screenName.replace(/\//g, '_');
    // Ensure it's not empty
    if (screenName && screenName.trim() !== '') {
      await analytics.setScreenName(screenName);
    }
  }
}, { immediate: true });

// Watcher pour réagir aux changements d'enfant courant
watch(() => appContextStore.currentChild, async (newChild, oldChild) => {
  // console.log('👶 [App.vue] Current child changed:', {
  //   newChildId: newChild?.id,
  //   newChildName: newChild?.name,
  //   oldChildId: oldChild?.id,
  //   oldChildName: oldChild?.name,
  //   timestamp: new Date().toISOString()
  // });

  // Protection : si on perd un enfant mais qu'on est sur une page d'enfant, le récupérer
  if (oldChild && !newChild && window.location.pathname.includes('/children/')) {
    // console.log('⚠️ [App.vue] Child lost while on child page, attempting recovery...');

    // Attendre un peu pour laisser le contexte se stabiliser
    setTimeout(async () => {
      await appContextStore.recoverChildFromURL();
    }, 100);
  }
}, { immediate: false });

// Force status bar to Light (dark text) – pour modales iOS où le texte devient blanc sur fond blanc
const setStatusBarStyleLight = async () => {
  try {
    try {
      await SystemBars.setStyle({ style: SystemBarsStyle.Light });
    } catch (_) {
      await StatusBar.setStyle({ style: Style.Light });
    }
  } catch (_) {
    // Web/PWA
  }
};

// Initialize System Bars (modern API) and Status Bar on app start – https://capacitorjs.com/docs/apis/system-bars & status-bar
const initializeStatusBar = async () => {
  try {
    // Android : les insets (barres système + clavier) sont gérés par le core
    // Capacitor (SystemBars, insetsHandling 'css') — plus de plugin dédié.

    // Legacy fallback: force overlay on older Android
    try {
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch (_) { /* overlay not supported on this platform */ }

    await setStatusBarStyleLight();
    debugLog('[App.vue] System bars set to Light (fullscreen)');
  } catch (error) {
    debugLog('[App.vue] System bars not available (web/PWA mode)');
  }
};

// iOS: quand une modale (presenting element) s'ouvre, la status bar peut passer en style clair (texte blanc) → on force Light (texte sombre)
const setupModalStatusBarFix = () => {
  if (Capacitor.getPlatform() !== 'ios') return;
  const handler = () => setStatusBarStyleLight();
  document.addEventListener('ionModalDidPresent', handler);
  document.addEventListener('didPresent', handler);
};

// Initialize
onMounted(async () => {
  // Boot is split into a CRITICAL path (auth → draft replay → context) that gates
  // the splash, and DEFERRED work (analytics user-id, version check, status-bar
  // settle) that runs after the first frame. Independent init is kicked off in
  // parallel up front instead of serialized, so the splash lifts as soon as the
  // first view can actually render.

  // Fire-and-forget: native status bar + analytics init start now, in parallel
  // with auth, and are only awaited later where their result is needed.
  const statusBarReady = initializeStatusBar().catch((e) => {
    console.warn('[App] status bar init failed (non-blocking):', e);
  });
  setupModalStatusBarFix();
  const analyticsReady = analytics.initialize().catch((e) => {
    console.warn('[App] analytics init failed (non-blocking):', e);
  });

  // Critical path: wait for auth to be ready then initialize context.
  await authStore.waitForAuth();

  // Self-healing: replay any uncommitted onboarding draft now that auth is known.
  // Wrapped in try/catch so a replay failure never blocks app boot.
  try {
    const { finalizePendingCommit } = useOnboardingFinalize();
    await finalizePendingCommit();
  } catch (err) {
    console.warn('[App] finalizePendingCommit failed (non-blocking):', err);
  }

  // Puis, dans la foulée : purge d'un onboarding « fantôme ». Le rattrapage
  // ci-dessus recrée le foyer manquant mais ne touche pas à l'état du
  // contrôleur ; un état resté bloqué à l'étape compte (auth revenue par lien
  // profond, finish() jamais appelé) survivait alors à tous les redémarrages et
  // renvoyait l'utilisateur sur /welcome à chaque lancement. Ordre important :
  // le commit d'abord, la purge ensuite, sinon un brouillon encore en attente
  // bloquerait la purge.
  try {
    await useOnboardingController().discardGhostOnboarding();
  } catch (err) {
    console.warn('[App] discardGhostOnboarding failed (non-blocking):', err);
  }

  // Web screenshot automation (Cypress) drives the app via a ?screenshot= query
  // instead of a native deep link. Route it through the same handler. On a fresh
  // (logged-out) visit this logs into the screenshot account and hard-reloads to
  // /tabs/tasks; consumePendingScreenshotIntent() below resumes the intent
  // after that reload.
  const shotUrl = screenshotUrlFromSearch(window.location.search);
  if (shotUrl) {
    await handleScreenshotDeepLink(shotUrl, router);
  }

  // If we were relaunched mid-screenshot-flow (login reload), resume it.
  await consumePendingScreenshotIntent(router);

  // Tournage de l'App Preview : le pilote ne se charge que si le lanceur a posé
  // un intent, et ne bloque pas le démarrage (il attend lui-même le foyer).
  if (localStorage.getItem(PREVIEW_INTENT_KEY)) {
    void import('@/services/app-preview/director').then((m) => m.runPendingPreview(router));
  }

  await initializeAppContext();

  // Hide splash screen now that auth + context are ready and the first view can render.
  // Wrapped in try/catch so a plugin failure never blocks the app.
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch (_) {
    // Web/PWA or plugin not available — splash is already hidden by then.
  }

  // ── Deferred, post-first-frame work ──
  // The UI is now visible; the following no longer gate the splash.
  await analyticsReady; // ensure analytics is ready before identity + session events
  if (authStore.user?.id) {
    try {
      await analytics.setUserId(authStore.user.id);
    } catch (e) {
      console.warn('[App] analytics setUserId failed (non-blocking):', e);
    }
  }
  // Version/update check runs over the visible UI instead of blocking boot.
  checkForUpdates().catch((e) => console.warn('[App] update check failed (non-blocking):', e));
  void statusBarReady; // settled in the background; nothing else waits on it

  // Initialize storage for photo uploads
  // console.log('📂 [App.vue] Initializing storage...');
  await initializeStorage();
  // console.log('✅ [App.vue] Storage initialized');

  // Track session start
  const lastSessionKey = 'harmonia_last_session';
  const lastSession = localStorage.getItem(lastSessionKey);
  const lastSessionDate = lastSession ? new Date(lastSession) : null;
  const now = new Date();

  // Calculate days since last session
  let lastSessionDaysAgo: number | undefined;
  if (lastSessionDate) {
    const diffTime = Math.abs(now.getTime() - lastSessionDate.getTime());
    lastSessionDaysAgo = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Save current session timestamp
  localStorage.setItem(lastSessionKey, now.toISOString());

  // Track session start
  analytics.logSessionStart(undefined, lastSessionDaysAgo);

  // Household children, used to aggregate completion counts below.
  const activeChildIds = (appContextStore.availableChildren || [])
    .map((c) => c.id)
    .filter(Boolean) as string[];

  // Track daily active (once per day)
  const todayDateStr = todayStr();
  const dailyActiveKey = 'harmonia_daily_active_' + todayDateStr;
  const dailyActiveTracked = localStorage.getItem(dailyActiveKey);
  if (!dailyActiveTracked && authStore.user) {
    setTimeout(async () => {
      try {
        // Sum today's completed tasks and unlocked bonuses across all the
        // household's children, in a single batched query.
        let tasksCompletedToday = 0;
        let rewardsUnlockedToday = 0;

        if (activeChildIds.length) {
          const rows = await dailyCompletionService.getDailyCompletionsForChildren(activeChildIds, todayDateStr);
          for (const row of rows.values()) {
            tasksCompletedToday += row?.tasks_completed || 0;
            rewardsUnlockedToday += row?.bonus_completed || 0;
          }
        }

        analytics.logDailyActive(tasksCompletedToday, rewardsUnlockedToday);
        localStorage.setItem(dailyActiveKey, 'true');
      } catch (error) {
        console.error('Error tracking daily active:', error);
      }
    }, 2000);
  }

  // Track weekly active (once per week)
  const weekStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
  const weekStartStr = toDateStr(weekStartDate);
  const weekEndStr = todayDateStr;
  const weekKey = 'harmonia_weekly_active_' + weekStartStr;
  const weeklyActiveTracked = localStorage.getItem(weekKey);
  if (!weeklyActiveTracked && authStore.user) {
    setTimeout(async () => {
      try {
        // Sum this week's completed tasks and earned points across all children.
        let tasksCompletedWeek = 0;
        let pointsEarnedWeek = 0;

        for (const childId of activeChildIds) {
          const completions = await dailyCompletionService.getDailyCompletions(childId, weekStartStr, weekEndStr);
          for (const c of completions) {
            tasksCompletedWeek += c?.tasks_completed || 0;
            pointsEarnedWeek += c?.points_earned || 0;
          }
        }

        analytics.logWeeklyActive(tasksCompletedWeek, pointsEarnedWeek);
        localStorage.setItem(weekKey, 'true');
      } catch (error) {
        console.error('Error tracking weekly active:', error);
      }
    }, 3000);
  }

  // console.log('✅ [App.vue] onMounted completed');
});
</script>

<style scoped>
/* Styles minimaux pour App.vue */
</style>